import { Slider, Stack } from "@mui/material";
import React from "react";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill.tsx";
import type { BarWidth, ChartStyleSettings, SeriesSettings } from "../../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../../store/hooks/useAppDispatch.ts";
import { fieldsStateActions } from "../../../../../store/thunks/fieldStatesThunks.ts";
import TypographyWithTooltip from "../../../../common/TypographyWithTooltip.tsx";
import { isBarSerie, toBarSerie } from "../../../../viewer/chart/helper.ts";

const Values: { value: number; label: BarWidth }[] = [
  { value: 10, label: "Thin" },
  { value: 20, label: "Small" },
  { value: 30, label: "Medium" },
  { value: 40, label: "Large" },
];

interface Props {
  settings: ChartStyleSettings;
  serie: SeriesSettings;
  readonlyMode: boolean;
}

export default function BarWidthItem({ settings, serie, readonlyMode }: Props) {
  const supported = isBarSerie(settings, serie);
  const barSerie = React.useMemo(() => toBarSerie(serie), [serie]);
  const dispatch = useAppDispatch();

  const [draft, setDraft] = React.useState<number>();
  const value = draft ?? (Values.find((v) => v.label === barSerie?.barWidth)?.value || 30);

  const formatValueLabel = React.useCallback((value: number) => {
    return Values.find((v) => v.value === value)?.label;
  }, []);

  const handleChangeCommitted = (newValue: number) => {
    const newBarWidth = Values.find((v) => v.value === newValue)?.label;
    if (newBarWidth !== undefined) {
      dispatch(fieldsStateActions.updateSeriesSettings({ name: serie.name, changes: { barWidth: newBarWidth } }));
    }
    setDraft(undefined);
  };

  if (!supported) {
    return <></>;
  }

  return (
    <Stack direction="row" useFlexGap spacing={3} sx={{ alignItems: "center" }}>
      <TypographyWithTooltip
        label="Bar Width"
        tooltipText="Bar width is relative and adapts to the window size. On the small size specifying Bar Width might not have an effect."
        typographyProps={{ sx: { color: "text.secondary" } }}
      />
      <HorizontalFill />
      <Slider
        value={value}
        valueLabelDisplay="auto"
        min={10}
        max={40}
        step={null}
        sx={{ py: 0, mr: 1, width: 120 }}
        marks={Values.map((v) => ({ value: v.value, label: "" }))}
        disabled={readonlyMode}
        valueLabelFormat={formatValueLabel}
        onChange={(_, value) => setDraft(value)}
        onChangeCommitted={(_, value) => handleChangeCommitted(value)}
      />
    </Stack>
  );
}
